import { format } from "date-fns";
import { FC, FormEvent } from "react";
import { ActionModal } from "shared/components/ActionModal";
import { Field, FieldErrorText, Textarea } from "shared/components/Spor";
import { useTypedForm } from "shared/hooks/useTypedForm";
import { z } from "zod";
import { useISirklogMessagePublish } from "features/CenterContent/RoleContent/ISirk/useISirkData";

type ISirkLogMessageModalProps = {
  isirkId: string;
  open: boolean;
  setopen: (open: boolean) => void;
};

const _createIncidentLogMessageSchema = z.object({
  message: z.string().min(1),
  isirkId: z.string().nullable(),
  incidentId: z.string().nullable(),
  date: z.string().nullable(),
});

export type CreateIncidentLogMessageSchema = z.infer<
  typeof _createIncidentLogMessageSchema
>;

const NewLogMessageModal: FC<ISirkLogMessageModalProps> = ({
  isirkId,
  open,
  setopen,
}) => {
  const { postIncidentLog, mutationStatus, resetMutation } =
    useISirklogMessagePublish();

  const {
    values,
    handleChange,
    reset: resetForm,
    startValidation,
    errors: formErrors,
  } = useTypedForm<{ message: string }>({ message: "" }, (formValues) => ({
    message:
      formValues.message.length > 0 ? undefined : "Loggmelding må fylles ut",
  }));

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const payload: CreateIncidentLogMessageSchema = {
      message: values.message,
      isirkId,
      incidentId: null,
      date: format(Date.now(), "yyyy-MM-dd"),
    };
    postIncidentLog(payload);
  };

  const onClose = () => {
    setopen(false);
    resetForm();
    resetMutation();
  };

  return open ? (
    <ActionModal
      title="Skriv ny loggmelding på I-Sirk"
      actionTitle="Send loggmelding"
      onClose={onClose}
      onSubmit={onSubmit}
      loading={mutationStatus === "pending"}
      isSuccess={mutationStatus === "success"}
      isError={mutationStatus === "error"}
      isSubmitDisabled={values.message.length === 0}
      successMessage="Loggmelding sendt"
      failureMessage="Kunne ikke sende loggmelding. Prøv igjen, eller kontakt IT hvis feilen vedvarer."
    >
      <Field invalid={!!formErrors.message} w="100%">
        <Textarea
          label="Skriv ny loggmelding"
          name="logmessage"
          value={values.message}
          onChange={(e) => handleChange({ message: e.target.value })}
          onBlur={() => startValidation()}
          aria-invalid={formErrors.message !== undefined}
          aria-errormessage="logmessage-error-message"
          minHeight="10rem"
        />
        <FieldErrorText>{formErrors.message}</FieldErrorText>
      </Field>
    </ActionModal>
  ) : null;
};

export default NewLogMessageModal;
